import axios from 'axios';
import { PAGINATION_LIMIT } from '../common/constants';

import { baseUrl, resolutionHeader } from '../Tenants';
import moment from 'moment';

import redirectToUpdate from './update';
import { logout } from '../helpers/upLoadHelper';
import { IServiceProviderResponse } from 'src/services/api/ServiceRequestDashboard/apiContracts';

export const client = axios.create({
  baseURL: '/data/',
});

client.interceptors.request.use((c) => {
  c.url = c.url + '.json';
  return c;
});

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface IApiRequestData {
  method: Method;
  headers: {
    Accept: string;
    'Content-Type': string;
    'Tenant-Id': string;
    Authorization?: string;
  };
  body?: string;
}

export const apiReq = async (
  url: string,
  method: Method,
  needsAuthorization: boolean,
  objBody?: any,
  isLoginPage?: boolean
) => {
  // Configure request data.
  const data: IApiRequestData = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Tenant-Id': resolutionHeader,
    },
  };

  // Add authorization if needed (for logged in pages).
  if (needsAuthorization) {
    try {
      const authToken: string = localStorage.getItem('auth');

      if (!authToken) {
        return isLoginPage
          ? { error: 'No authentication token found' }
          : await logout();
      }

      data.headers.Authorization = 'Bearer ' + JSON.parse(authToken);
    } catch (tokenError) {
      console.error('Token error:', tokenError);
      return isLoginPage
        ? { error: 'Authentication token error' }
        : await logout();
    }
  }

  // Add request body if provided.
  if (objBody !== null) {
    data.body = JSON.stringify(objBody);
  }

  try {
    const response: Response = await fetch(url, data);

    // Handle unauthorized access for login page.
    if (response.status === 401 && isLoginPage) {
      throw new Error('Invalid login attempt');
    }

    // Handle unauthorized access for non-login pages.
    if (response.status === 401 && !isLoginPage) {
      await logout();
      throw new Error('Unauthorized access');
    }

    if(response.status === 410) {
      redirectToUpdate();
    }

    return response;
  } catch (error) {
    console.error(`API request error for ${url}:`, error);
    return {
      error: error.message || 'Request failed',
      isError: true,
    };
  }
};

export const createQueryParameters = (params) => {
  let query = '?';
  for (let key in params) {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key].length === 0
    )
      continue;
    if (params[key] instanceof Array) {
      query += params[key].reduce((acc, value) => {
        return acc + key + '=' + value + '&';
      }, '');
    } else {
      query += key + '=' + params[key] + '&';
    }
  }
  return query;
};

export const signIn = async (data) => {
  return await apiReq(
    baseUrl + 'api/UserManagement/Login',
    'POST',
    false,
    data,
    true // We're calling the apiReq method confirming that we're coming via the login page.
  );
};

export const signUp = (data) => apiReq('', 'POST', true, data);
export const activateUser = (data) =>
  apiReq(baseUrl + 'api/UserManagement/ActivateUser', 'POST', true, data);
export const forgotPassword = (mailId) =>
  apiReq(
    baseUrl + 'api/UserManagement/ForgotPassword/' + mailId,
    'POST',
    false,
    null
  );
export const forceChangePassword = (userId, data) =>
  apiReq('', 'POST', false, data);
export const resetPassword = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/ForgotPassword?email=' + data,
    'POST',
    true,
    null
  );
export const getAuthenticatedUser = () => client.get('/auth/me');
export const changePassword = (data) =>
  apiReq(baseUrl + 'api/UserManagement/ChangePassword', 'POST', true, data);
export const validateSlugCode = (slugCode) => apiReq('', 'GET', false, null);

export const updateUserProfile = (data) =>
  apiReq(baseUrl + 'api/UserManagement/UpdateProfile', 'PUT', true, data);

//Assets API endpoints
export const getAssetsBySuiteId = (suiteId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitAreaAssets/' +
      suiteId +
      '/' +
      userId +
      '/' +
      role,
    'GET',
    true,
    null
  );
export const getAssetById = (id) => client.get(`assets/${id}`);
export const getAssetNotes = (id, query) =>
  client.get(`assets/${id}/notes`, { params: { query } });
export const getAssetDocuments = (id, query) =>
  client.get(`assets/${id}/documents`, { params: { query } });
export const addAsset = (data) =>
  apiReq(baseUrl + 'api/Properties/AddSuiteAsset', 'POST', true, data);
export const updateAsset = (data) =>
  apiReq(baseUrl + 'api/Properties/UpdateSuiteAsset', 'PUT', true, data);
export const deleteAsset = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Assets/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );

//  Properties API end points
export const getProperties = (userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/PropertiesByUser/' +
      userId +
      '/' +
      role +
      '/Properties',
    'GET',
    true,
    null
  );
export const saveProperty = (data) =>
  apiReq(baseUrl + 'api/Properties/Properties', 'POST', true, data);
export const updateProperty = (data) =>
  apiReq(baseUrl + 'api/Properties/Properties', 'PUT', true, data);
export const deleteProperty = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Properties/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );
export const getPropertyById = (id) =>
  apiReq(baseUrl + 'api/Properties/Properties/' + id, 'GET', true, null);
export const getPropertyByHomeownerId = (id) =>
  apiReq(
    baseUrl + 'api/Properties/HomeOwnerProperties/' + id,
    'GET',
    true,
    null
  );

export const getSuitesByUser = (userId, role) =>
  apiReq(
    baseUrl + 'api/Properties/HomeOwnerSuiteList/' + userId + '/' + role,
    'GET',
    true,
    null
  );
export const getPropertySuites = (id, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitsbyProperty/' +
      id +
      '/' +
      userId +
      '/' +
      role +
      '/Units',
    'GET',
    true,
    null
  );
export const getPropertySuitesForTable = (id, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitListbyProperty/' +
      id +
      '/' +
      userId +
      '/' +
      role +
      '/Units',
    'GET',
    true,
    null
  );
export const getPropertySuiteById = (id) =>
  apiReq(baseUrl + 'api/Properties/Units/' + id, 'GET', true, null);
export const getPropertiesSuites = (params) =>
  apiReq(baseUrl + 'api/Properties/UnitsbyPropertyList', 'POST', true, params);
export const updateSuiteSynchronization = (suiteId, toggle) =>
  apiReq(
    baseUrl +
      'api/properties/toggle-suite-synchronization/' +
      suiteId +
      '/' +
      toggle,
    'POST',
    true,
    null
  );

export const getTabList = () =>
  apiReq(baseUrl + 'api/Properties/Modules', 'GET', true, null);
export const getAllowedTabList = (id) =>
  apiReq(baseUrl + 'api/Properties/ModulePermissions/' + id, 'GET', true, null);

export const getPropertyNotes = (id, query) =>
  client.get(`properties/${id}/notes`, { params: { query } });
export const getPropertyDocuments = (id, query) =>
  client.get(`properties/${id}/documents`, { params: { query } });
export const getPropertyServiceRequests = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/GetServiceRequestByProperty/' + id,
    'GET',
    true,
    null
  );
export const getSuiteOnBoardingConveyanceFormListByPropertyCSV = (propertyId) =>
  apiReq(
    baseUrl +
      'api/Properties/SuiteOnBoardingConveyanceFormListByPropertyCSV/' +
      propertyId,
    'GET',
    true,
    null
  );

export const getServiceRequestByPropertyIdUserIdRole = (
  propertyId,
  userId,
  role,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByPropertyPagination/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );

export const getServiceRequestByCommonIdUserIdRole = (
  propertyId,
  role,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByCommonArea/' +
      propertyId +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );

//Suites API endpoints
export const addSuite = (suite) =>
  apiReq(baseUrl + 'api/Properties/Units', 'POST', true, suite);

export const addCopySuite = (suite) =>
  apiReq(baseUrl + 'api/Properties/CopyUnit', 'POST', true, suite);

export const updateSuite = (suite) =>
  apiReq(baseUrl + 'api/Properties/Units', 'PUT', true, suite);
export const deleteUnit = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Units/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );
export const getSuiteById = (id) => client.get(`suites/${id}`);
export const getSuiteAssets = (suiteId) =>
  apiReq(baseUrl + 'api/Properties/Assets/' + suiteId, 'GET', true, null);
export const getSuiteNotes = (id, query) =>
  client.get(`suites/${id}/notes`, { params: { query } });
export const getSuiteDocuments = (id, query) =>
  client.get(`suites/${id}/documents`, { params: { query } });
export const getSuiteStatuses = () =>
  apiReq(baseUrl + 'api/Properties/SuiteStatuses', 'GET', true, null);
export const getSuiteServiceRequests = (id, role, count, text) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestBySuitePagination/' +
      id +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );
export const getSuiteAreas = () =>
  apiReq(baseUrl + 'api/Catalog/Areas', 'GET', true, null);
export const getSuiteAssetAreas = (id) => {
  throw new Error('Removed Api');
};
export const setWalkThroughStatus = (status) =>
  apiReq(
    baseUrl + 'api/Walkthrough/UpdateWalkThroughStatus',
    'PUT',
    true,
    status
  );

export const getWalkthroughServiceRequestListByAsset = (assetId, id) => {
  throw new Error('Removed Api');
};

export const getLocationsById = (id) => {
  throw new Error('Removed Api');
};

export const getAssetsByLocationId = (locationId, id) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/WalkthroughSubAreasDrDown/' +
      locationId +
      '/' +
      id,
    'GET',
    true,
    null
  );

export const getContacts = (id, role) =>
  apiReq(
    baseUrl + 'api/UserManagement/Users/' + id + '/' + role + '/List',
    'GET',
    true,
    null
  );
export const getContactById = (contactId, userId) =>
  apiReq(
    baseUrl + 'api/UserManagement/users/' + contactId + '/' + userId,
    'GET',
    true,
    null
  );
export const getContactNotes = (id, query) =>
  client.get(`contacts/${id}/notes`, { params: { query } });
export const getContactDocuments = (id, query) =>
  client.get(`contacts/${id}/documents`, { params: { query } });
export const getContactServiceRequests = (id, query) =>
  client.get(`contacts/${id}/service-requests`, { params: { query } });
export const saveContact = (data) =>
  apiReq(baseUrl + 'api/UserManagement/Users', 'POST', true, data);
export const sendInvitationToContact = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/SendActivateLinkToUsers',
    'POST',
    true,
    data
  );
export const updateContact = (data) =>
  apiReq(baseUrl + 'api/UserManagement/Users', 'PUT', true, data);
export const deleteContactById = (id) =>
  apiReq(
    baseUrl + 'api/UserManagement/Users/{' + id + '}',
    'DELETE',
    true,
    null
  );
export const getAllRoles = () =>
  apiReq(baseUrl + 'api/UserManagement/roles', 'GET', true, null);
export const fetchAvailableServiceTimes = (id, servicePreferences) =>
  apiReq(
    baseUrl +
      'api/UserManagement/Users/' +
      id +
      '/' +
      moment().format('YYYY-MM-DD HH:mm:ss') +
      '/' +
      localStorage.getItem('theme') +
      '/AvailableServiceTimes/?serviceRequestPreferences=' +
      servicePreferences,
    'GET',
    true,
    null
  );
export const fetchPropertyDeveloperAvailableTimes = (id, servicePreferences) =>
  apiReq(
    baseUrl +
      'api/UserManagement/Users/' +
      id +
      '/' +
      moment().format('YYYY-MM-DD HH:mm:ss') +
      '/PropertyDeveloperAvailableTimes/?serviceRequestPreferences=' +
      servicePreferences,
    'GET',
    true,
    null
  );

//ServiceRequests API endpoints
export const getServiceRequests = (id, role) =>
  id
    ? apiReq(
        baseUrl +
          'api/ServiceRequest/GetServiceRequestsByUser/' +
          id +
          '/' +
          role,
        'GET',
        true,
        null
      )
    : apiReq(baseUrl + 'api/ServiceRequest/ServiceRequest', 'GET', true, null);
export const fetchServiceRequestsListByUser = (
  id,
  role,
  loginUserId,
  loginUserRole,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListByUserPagination/' +
      id +
      '/' +
      role +
      '/' +
      loginUserId +
      '/' +
      loginUserRole +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );
export const fetchServiceRequestByItemId = (
  itemId,
  userId,
  role,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByCatalogPagination/' +
      itemId +
      '/' +
      userId +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );
export const saveServiceRequest = (serviceRequest) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequest',
    'POST',
    true,
    serviceRequest
  );
export const saveScheduledAppointment = (scheduledAppointment) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ScheduledAppointment',
    'POST',
    true,
    scheduledAppointment
  );
export const saveStatus = (id, status) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + id + '/Status',
    'PATCH',
    true,
    status
  );
export const getServiceRequestDocuments = (id) =>
  apiReq(baseUrl + 'api/Resource/Documents/' + id, 'GET', true, null);
export const getServiceRequestNotes = (id) =>
  apiReq(baseUrl + 'api/Resource/Resources/' + id, 'GET', true, null);
export const getServiceRequestById = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequest/' + id,
    'GET',
    true,
    null
  );
export const updateServiceRequestOneYearWarrantyIssueStatus = (data) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateServiceRequestOneYearWarrantyIssue',
    'PUT',
    true,
    data
  );
export const getServiceRequestPdfById = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestDetailsPDF/' + id,
    'GET',
    true,
    null
  );
export const fetchServiceRequestActivityById = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestActivityLog/' + id,
    'GET',
    true,
    null
  );
export const deleteServiceRequest = (id) =>
  apiReq(baseUrl + 'api/ServiceRequest/Delete/' + id, 'DELETE', true, null);

//Items API endpoints
export const getItems = (id, role) =>
  apiReq(baseUrl + 'api/Catalog/Items/' + id + '/' + role, 'GET', true, null);
export const getAllItemTypes = () =>
  apiReq(baseUrl + 'api/Catalog/ItemType', 'GET', true, null);
export const saveItem = (data) =>
  apiReq(baseUrl + 'api/Catalog/Items', 'POST', true, data);
export const deleteItem = (id) =>
  apiReq(baseUrl + 'api/Catalog/Items/' + id, 'DELETE', true, null);
export const updateItem = (data) =>
  apiReq(baseUrl + 'api/Catalog/Items', 'PUT', true, data);
export const getItemById = (id) => client.get(`items/${id}`);
export const getItemNotes = (id, query) =>
  client.get(`items/${id}/notes`, { params: { query } });
export const getItemDocuments = (id, query) =>
  client.get(`items/${id}/documents`, { params: { query } });

// FAQS, NOTES, DOCUMENTS
export const updateFaqsNotesDocuments = (data) =>
  apiReq(baseUrl + 'api/Resource/Resources', 'PUT', true, data);
export const deleteFaqsNotesDocuments = (id) =>
  apiReq(baseUrl + 'api/Resource/Resources/' + id, 'DELETE', true, null);
export const fetchFaqsNotesDocuments = (id) =>
  apiReq(baseUrl + `api/Resource/Resources/${id}`, 'GET', true, null);
export const fetchFaqsNotesDocumentForAssetByAssetId = (id, itemdata) =>
  apiReq(
    baseUrl + 'api/Resource/AssetResourcesDetails/' + id + '/' + itemdata,
    'GET',
    true,
    null
  );
export const fetchServiceRequestFaqsNotesDocuments = (id) =>
  apiReq(
    baseUrl + `api/Resource/ServiceRequestDocuments/${id}`,
    'GET',
    true,
    null
  );
export const addFaqsNotesDocuments = (data) =>
  apiReq(baseUrl + 'api/Resource/Resources', 'POST', true, data);
export const getIssueTypes = () =>
  apiReq(baseUrl + 'api/Resource/IssueTypes', 'GET', true, null);
export const getTicketStatus = () =>
  apiReq(baseUrl + 'api/Resource/TicketStatus', 'GET', true, null);
export const getNotes = (id) =>
  apiReq(baseUrl + 'api/Resource/Resources/' + id, 'GET', true, null);
export const getTypeaheadResults = (id, role, query) =>
  apiReq(
    baseUrl + 'api/common/ListFilter/' + id + '/' + role + '/' + query,
    'GET',
    true,
    null
  );
export const fetchMessages = (data) =>
  apiReq(baseUrl + 'api/Common/GetMessages', 'POST', true, data);
export const postMessage = (data) =>
  apiReq(baseUrl + 'api/Common/SendMessage', 'POST', true, data);
export const sendEmailNotification = (data) =>
  apiReq(baseUrl + 'api/Common/SendEmailOnVideoChat', 'POST', true, data);

export const getUserProfileDetails = (userId) =>
  apiReq(
    baseUrl + 'api/UserManagement/GetSettings/' + userId,
    'GET',
    true,
    null
  );
export const updateCompanyDetails = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/UpdateCompanySettings',
    'PUT',
    true,
    data
  );
export const updateNotificationDetails = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/UpdateNotificationSettings',
    'PUT',
    true,
    data
  );

export const getSystemLevelNotifications = () =>
  apiReq(
    `${baseUrl}api/settings/system-level-notifications`,
    'GET',
    true,
    null
  );
export const updateSystemLevelNotification = (data) =>
  apiReq(
    `${baseUrl}api/settings/update-system-level-notification`,
    'PUT',
    true,
    data
  );

// property walkk through api's
export const saveConfigType = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/Walkthrough', 'POST', true, data);

export const updateConfigType = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/Walkthrough', 'PUT', true, data);

export const getAreasAndSubAreasBySuiteId = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/GetWalkthroughAreas/' + walkthroughId,
    'GET',
    true,
    null
  );

export const deleteSubArea = (subAreaId) => {
  throw new Error('Removed Api');
};

export const saveSubAreaDescription = (data) => {
  throw new Error('Removed Api');
};

export const fetchSubAreaData = (subAreaId, walkthroughId, tmps) => {
  throw new Error('Removed Api');
};

export const getWalkthroughSuites = (propertyId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughUnitsDrDown/' + propertyId,
    'GET',
    true,
    null
  );

export const saveCopyFromExisting = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/CopyWalkthrough', 'POST', true, data);

export const fetchAreaData = (walkthroughId, areaId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkThroughArea/' + walkthroughId + '/' + areaId,
    'GET',
    true,
    null
  );

export const saveAreaDescription = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/WalkThroughArea', 'PUT', true, data);

export const updateWalkthroughChecklist = (data) => {
  throw new Error('Removed Api');
};

export const fetchConfigureTypeWalkThroughData = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/Walkthrough/' + walkthroughId,
    'GET',
    true,
    null
  );

export const fetchSuiteCheckList = (walkthroughId) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/WalkthroughAreasChecklist/{' +
      walkthroughId +
      '}',
    'GET',
    true,
    null
  );

export const getAreaAndSubAreaStatus = (walkthroughId) => {
  throw new Error('Removed Api');
};

export const getNewAuthorizationToken = (userId) =>
  apiReq(baseUrl + 'api/UserManagement/Token/' + userId, 'GET', true, null);

export const updateSubAreaCheckStatus = (subAreaId, status, walkthroughId) => {
  throw new Error('Removed Api');
};

export const fetchLatestMessages = (suiteId) =>
  apiReq(
    baseUrl + `api/Common/GetMessages?$top=5&$orderBy=sentOn desc`,
    'POST',
    true,
    {
      referenceId: suiteId,
      referenceType: 'Suite',
    }
  );

export const fetchLatestFaqsNotesDocuments = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/DashboardResourcesBySuite/' +
      suiteId +
      '?$filter=type eq ' +
      "'DOCUMENTS'" +
      '&$orderBy=createdOn desc&$top=5',
    'GET',
    true,
    null
  );

export const fetchMaintenanceGuide = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/DashboardMaintenanceGuideBySuite/' +
      suiteId +
      '?$top=5',
    'GET',
    true,
    null
  );

export const fetchLatestServiceRequests = (suiteId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListBySuite/' +
      suiteId +
      '?$top=5&$orderBy=createdDate desc',
    'GET',
    true,
    null
  );

export const fetchPropertyManagerDetails = (userId) =>
  apiReq(
    baseUrl + 'api/Properties/GetPropertyManagerDetails/' + userId,
    'GET',
    true,
    null
  );
export const fetchPropertyDetailsByProperty = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyDetailsByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchWarrantyStatus = (suiteId) =>
  apiReq(
    baseUrl + 'api/Properties/AssetsWarrantExpirationBySuite/' + suiteId,
    'GET',
    true,
    null
  );

export const getRecentActivity = (suiteId) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestActivitysBySuite/' + suiteId,
    'GET',
    true,
    null
  );

export const fetchPropertyWalkthrough = (suiteId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/HomeOwnerWalkthroughStatusBySuite/' + suiteId,
    'GET',
    true,
    null
  );

export const fetchCalendlyLink = (email) =>
  apiReq(
    baseUrl + 'api/UserManagement/UserCalendarLink/' + email,
    'GET',
    true,
    null
  );

export const onEventScheduled = (params) =>
  apiReq(baseUrl + 'api/Common/CalendlySchedules', 'POST', true, params);

export const getLocations = (value) =>
  apiReq(baseUrl + 'api/Common/GetGoogleLocations/' + value, 'GET', true, null);

export const fetchWalkThroughServiceList = (walkthroughId) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/GetWalkthroughServiceRequestlistData/' +
      walkthroughId,
    'GET',
    true,
    null
  );

export const updateWalkThroughRoomSequence = (params) =>
  apiReq(
    baseUrl + 'api/Walkthrough/UpdateWalkThroughRoomSequence',
    'PUT',
    true,
    params
  );

export const fetchServiceRequestsByAssetId = (assetId, walkthroughId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/GetServiceRequestByAsset/' +
      assetId +
      '/' +
      walkthroughId,
    'GET',
    true,
    null
  );

export const saveAnnouncement = (params) =>
  apiReq(baseUrl + 'api/Common/Announcement', 'POST', true, params);

export const getAnnouncements = (id, role) =>
  apiReq(
    baseUrl + 'api/Common/Announcement/' + id + '/' + role,
    'GET',
    true,
    null
  );

export const getAnnouncementById = (id) =>
  apiReq(baseUrl + 'api/Common/AnnouncementById/' + id, 'GET', true, null);

export const getLatestAnnouncements = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Common/AnnouncementsBySuite/' +
      suiteId +
      '?$orderby=announcementDate desc&&$top=2',
    'GET',
    true,
    null
  );

export const deleteAnnouncement = (id) =>
  apiReq(baseUrl + 'api/Common/Announcement/' + id, 'DELETE', true, null);

export const getAnnouncementBuildings = (id) =>
  apiReq(baseUrl + 'api/Properties/PropertyBuildings/' + id, 'GET', true, null);
export const getAnnouncementLevels = (id) =>
  apiReq(
    baseUrl + 'api/Properties/BuildingLevelsProperty/' + id,
    'GET',
    true,
    null
  );

export const getAnnouncementUnits = (params) =>
  apiReq(
    baseUrl + 'api/Common/AnnouncementBuildingLevels',
    'POST',
    true,
    params
  );

export const fetchSuiteWalkThroughs = (suiteId, roleName) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughbySuite/' + suiteId + '/' + roleName,
    'GET',
    true,
    null
  );

export const fetchCommonAreas = () =>
  apiReq(baseUrl + 'api/Properties/CommonAreas', 'GET', true, null);

export const savePropertyCommonAreas = (data) =>
  apiReq(baseUrl + 'api/Properties/PropertyCommonAreas', 'PUT', true, data);

export const fetchPropertyCommonAreas = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyCommonAreas/' + propertyId,
    'GET',
    true,
    null
  );

export const validateEndingWalkThroughEarly = (walkthroughId) => {
  throw new Error('Removed Api');
};

export const fetchSuiteUsers = (suiteId) =>
  apiReq(baseUrl + 'api/Common/UsersListBySuite/' + suiteId, 'GET', true, null);

export const updateServiceRequest = (serviceRequest) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequest',
    'PUT',
    true,
    serviceRequest
  );

export const fetchfilteredServiceRequestByStatusAndDays = (
  id,
  role,
  status,
  days,
  count,
  text,
  filterValues
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/GetServiceRequestListByUserPagination/' +
      id +
      '/' +
      role +
      '/' +
      status +
      '/' +
      days +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      filterValues,
    'GET',
    true,
    null
  );

export const createServiceRequestByGroup = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/GroupingServiceRequests',
    'POST',
    true,
    params
  );

export const fetchGroupServiceRequest = (
  groupId,
  id,
  role,
  status,
  days,
  filterValues
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListByGroup/' +
      groupId +
      '/' +
      id +
      '/' +
      role +
      '/' +
      status +
      '/' +
      days +
      filterValues,
    'GET',
    true,
    null
  );

export const deleteServiceRequestGroup = (groupingServiceRequestId, userId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/DeleteGroupingServiceRequest/' +
      groupingServiceRequestId +
      '/' +
      userId,
    'DELETE',
    true,
    null
  );

export const deleteServiceRequestFromGroup = (serviceRequestId, usersId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/DeleteServiceRequestFromGrouping/' +
      serviceRequestId +
      '/' +
      usersId,
    'DELETE',
    true,
    null
  );

export const fetchEmailTemplates = () =>
  apiReq(baseUrl + 'api/Common/HtmlEmailTemplateList', 'GET', true, null);

export const updateEmailTemplate = (params) =>
  apiReq(baseUrl + 'api/Common/HtmlEmailTemplate', 'PUT', true, params);

export const fetchfilteredServiceRequestByStatusCount = (
  id,
  role,
  status,
  days,
  count,
  text,
  filterValues
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestStatusCountsByUser/' +
      id +
      '/' +
      role +
      '/' +
      status +
      '/' +
      days +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      filterValues,
    'GET',
    true,
    null
  );

export const fetchServicesProviderAndPropertyDeveloperList = (
  LoginUserId,
  Role
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServicesProvidedandPropertyDeveloperList/' +
      LoginUserId +
      '/' +
      Role,
    'GET',
    true,
    null
  );

export const getfetchCommonIssues = (assetId) =>
  apiReq(
    baseUrl + 'api/Common/CommonIssuesByAsset/' + assetId,
    'GET',
    true,
    null
  );

export const saveCommonIssue = (params) =>
  apiReq(baseUrl + 'api/Common/CommonIssues', 'POST', true, params);

export const updateCommonIssue = (params) =>
  apiReq(baseUrl + 'api/Common/CommonIssues', 'PUT', true, params);

export const deleteCommonIssue = (id) =>
  apiReq(baseUrl + 'api/Common/CommonIssues/' + id, 'DELETE', true, null);

export const fetchLatestIssues = (itemId, issueId) =>
  apiReq(
    baseUrl +
      'api/Common/TopCommonIssues/' +
      itemId +
      '/' +
      issueId +
      '?$top=3',
    'GET',
    true,
    null
  );

export const fetchCommonAreaAssets = (commonAreaId) =>
  apiReq(
    baseUrl + 'api/Properties/CommonArea/' + commonAreaId + '/Assets',
    'GET',
    true,
    null
  );

export const saveCommonAreaAsset = (params) =>
  apiReq(baseUrl + 'api/Properties/CommonAreaAsset', 'POST', true, params);

export const updateCommonAreaAsset = (params) =>
  apiReq(baseUrl + 'api/Properties/CommonAreaAsset', 'PUT', true, params);

export const deleteCommonAreaAsset = (commonAreaAssetId) =>
  apiReq(
    baseUrl + 'api/Properties/CommonAreaAsset/' + commonAreaAssetId,
    'DELETE',
    true,
    null
  );

export const fetchPropertyManagersByPropertyId = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyManagerDetailsByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchPropertyCounts = (propertyId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/CountsByProperty/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role,
    'GET',
    true,
    null
  );

export const fetchSuiteCounts = (suiteId, role) =>
  apiReq(
    baseUrl + 'api/Properties/CountsBySuite/' + suiteId + '/' + role,
    'GET',
    true,
    null
  );

export const addExistingPropertyDeveloper = (data) =>
  apiReq(
    baseUrl + 'api/Properties/CopyPropertyManagerToProperty',
    'POST',
    true,
    data
  );

export const fetchContactCounts = (
  contactId,
  role,
  loginUserId,
  loginUserRole
) =>
  apiReq(
    baseUrl +
      'api/Properties/CountsByUser/' +
      contactId +
      '/' +
      role +
      '/' +
      loginUserId +
      '/' +
      loginUserRole,
    'GET',
    true,
    null
  );

export const updateBulkStatus = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateBulkServiceRequestStatus',
    'PUT',
    true,
    params
  );

export const fetchReports = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestListByProperties',
    'POST',
    true,
    params
  );

export const fetchUserActivityReport = (params) =>
  apiReq(
    baseUrl + 'api/UserManagement/GetUserActivityLogs',
    'POST',
    true,
    params
  );

export const fetchUserStatusReports = (propertyIds) =>
  apiReq(
    baseUrl + 'api/common/UserStatusReportList/' + propertyIds,
    'GET',
    true,
    null
  );

export const fetchServiceRequestStatsReport = (data) =>
  apiReq(
    baseUrl + 'api/report/service-requests-stats' + createQueryParameters(data),
    'GET',
    true,
    null
  );

export const deletePropertyDeveloper = (id, propertyId) =>
  apiReq(
    baseUrl +
      `api/Properties/DeletePropertyManagerFromProperty/${propertyId}/${id}`,
    'DELETE',
    true,
    null
  );

export const validateUser = (params) =>
  apiReq(baseUrl + 'api/UserManagement/UserVerification', 'PUT', false, params);

export const setReport = (params) =>
  apiReq(baseUrl + 'api/UserManagement/UserActivityLog', 'POST', true, params);

export const fetchMainServiceRequestCSVExportDownloadList = (
  id,
  role,
  status,
  days,
  params
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListfilter/' +
      id +
      '/' +
      role +
      '/' +
      status +
      '/' +
      days +
      params,
    'GET',
    true,
    null
  );
export const fetchSelectedServiceRequestCSVExportDownloadList = (ids) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestSelectedListForCSV/',
    'POST',
    true,
    ids
  );
export const fetchMainServiceRequestPDFExportDownloadList = (
  id,
  role,
  status,
  days,
  params
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/GetServiceRequestListPDF/' +
      id +
      '/' +
      role +
      '/' +
      status +
      '/' +
      days +
      params,
    'GET',
    true,
    null
  );
export const fetchSelectedServiceRequestPDFExportDownloadList = (id, params) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestSelectedListForPDF/' +
      params +
      '/' +
      id,
    'GET',
    true,
    null
  );
export const fetchMainServiceRequestPDFDownloadList = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UserPDFRequests/' + id,
    'GET',
    true,
    null
  );
export const fetchPropertyServiceRequestCSVExportDownloadList = (
  propertyId,
  userId,
  role,
  searchString
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByPropertyCSV/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role +
      '/' +
      searchString,
    'GET',
    true,
    null
  );
export const fetchSuiteServiceRequestCSVExportDownloadList = (
  suiteId,
  role,
  searchString
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestBySuiteCSV/' +
      suiteId +
      '/' +
      role +
      '/' +
      searchString,
    'GET',
    true,
    null
  );
export const fetchContactServiceRequestCSVExportDownloadList = (
  userId,
  role,
  searchString
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListByUserCSV/' +
      userId +
      '/' +
      role +
      '/' +
      searchString,
    'GET',
    true,
    null
  );
export const fetchCatalogItemServiceRequestCSVExportDownloadList = (
  itemId,
  userId,
  role,
  searchString
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByCatalogCSV/' +
      itemId +
      '/' +
      userId +
      '/' +
      role +
      '/' +
      searchString,
    'GET',
    true,
    null
  );
export const fetchCommonAreaServiceRequestCSVExportDownloadList = (
  commonAreaId,
  role,
  searchString
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByCommonAreaCSV/' +
      commonAreaId +
      '/' +
      role +
      '/' +
      searchString,
    'GET',
    true,
    null
  );

export const onFetchLatestVideos = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/HomeownerEmbedVideosBySuite/' +
      suiteId +
      '?orderBy=createdOn desc',
    'GET',
    true,
    null
  );

export const downloadRequests = (walkthroughId, suiteId) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/DownloadWalkThroughReview/' +
      walkthroughId +
      '/' +
      suiteId,
    'GET',
    true,
    null
  );

export const downloadPDF = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughReviewPDFURL/' + walkthroughId,
    'GET',
    true,
    null
  );

export const fetchMaintainanceGuideBySuiteId = (id) =>
  apiReq(
    baseUrl + 'api/Properties/AssetsMaintenanceGuidesBySuite/' + id,
    'GET',
    true,
    null
  );

export const userActivateStatus = (id) =>
  apiReq(baseUrl + 'api/UserManagement/UserStatus/' + id, 'PUT', false, null);

export const getPropertyBuildings = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyBuildings/' + propertyId,
    'GET',
    true,
    null
  );

export const getBuildingLevels = (buildingId) =>
  apiReq(
    baseUrl + 'api/Properties/BuildingLevels/' + buildingId,
    'GET',
    true,
    null
  );

export const addExistingUser = (data) =>
  apiReq(
    baseUrl + 'api/Properties/HomeownerAssociateToSuite',
    'PUT',
    true,
    data
  );

export const CheckExistingParkingStallStorageLockers = (
  propertyId,
  name,
  type
) =>
  apiReq(
    `${baseUrl}api/Properties/CheckExistingParkingStallStorageLockers/${propertyId}/${name}/${type}`,
    'GET',
    true,
    null
  );

export const getServiceProviders = async (params: any) => {
  try {
    const response: any = await apiReq(
      baseUrl + 'api/ServiceRequest/ServicesProvidedListByServiceType',
      'POST',
      true,
      params
    );

    if (!response.ok) {
      const errorMessage = `Failed to fetch service providers: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Unexpected error in getServiceProviders:', error);
    throw error;
  }
};

export const getServiceProvidersFilteredByPropertyId = async (
  propertyId: string
): Promise<IServiceProviderResponse> => {
  try {
    const url: string =
      baseUrl + 'api/usermanagement/service-providers?propertyId=' + propertyId;
    const response: any = await apiReq(url, 'GET', true, null);

    if (!response.ok) {
      const errorMessage = `Failed to fetch service providers: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error(
      'Unexpected error in getServiceProvidersFilteredByPropertyId:',
      error
    );
    throw error;
  }
};

export const onSubmitKeyhandOffProcess = (params) =>
  apiReq(baseUrl + 'api/Properties/KeyFobsSignatures', 'POST', true, params);

export const reRenderKeyPickupPdf = (suiteId) =>
  apiReq(
    baseUrl + `api/properties/re-render-key-pickup-pdf/${suiteId ?? ''}`,
    'GET',
    true,
    null
  );

export const onFetchSuiteDetails = (suiteId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/KeyFobsDetailsBysuite/' +
      suiteId +
      '/' +
      userId +
      '/' +
      role,
    'GET',
    true,
    null
  );

export const onWalkthroughScheduled = (params) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkThroughReviewSchedule',
    'POST',
    true,
    params
  );

export const saveHomeOwnerOnboarding = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoarding',
    params && params.id ? 'PUT' : 'POST',
    true,
    params
  );

export const saveNotaryForm = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingConveyanceForm',
    params && params.id ? 'PUT' : 'POST',
    true,
    params
  );

export const fetchHomeOwnerOnboarding = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingList/' + propertyId,
    'GET',
    true,
    null
  );

export const changeOrdersSequence = (params) =>
  apiReq(baseUrl + 'api/Properties/SuiteOnBoardingOrder', 'PUT', true, params);

export const getPropertyStages = () =>
  apiReq(baseUrl + 'api/Properties/ConstructionStages', 'GET', true, null);

export const getPropertyStatuses = () =>
  apiReq(baseUrl + 'api/Properties/PropertyStatuses', 'GET', true, null);
export const updateSuiteOnBoardingFAQsOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingFAQsOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingChecklistsOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingChecklistOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingKeysOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingKeyInformationOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingOffersOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingOffersOrder',
    'PUT',
    true,
    params
  );
export const fetchPropertyStages = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyConstructionStages/' + propertyId,
    'GET',
    true,
    null
  );

export const onFetchMoreDetails = (id) =>
  apiReq(baseUrl + 'api/Properties/UnitSparkDetails/' + id, 'GET', true, null);

export const onFetchContactMoreDetails = (id) =>
  apiReq(
    baseUrl + 'api/UserManagement/UserSparkDetails/' + id,
    'GET',
    true,
    null
  );

export const fetchOnboardingOrders = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardings/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchOnboardingOrderDetails = (
  propertyId,
  orderId,
  suiteId,
  userId
) =>
  apiReq(
    baseUrl +
      'api/Properties/SuiteOnBoarding/' +
      propertyId +
      '/' +
      orderId +
      '/' +
      suiteId +
      '/' +
      userId,
    'GET',
    true,
    null
  );

export const onChangeChecklistStatus = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingChecklist',
    'PUT',
    true,
    params
  );

export const onAddChecklistNote = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistNote',
    'POST',
    true,
    params
  );

export const onAddChecklistDocument = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistDocuments',
    'PUT',
    true,
    params
  );

export const onUpdateChecklistNote = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistNote',
    'PUT',
    true,
    params
  );

export const onDeleteOrder = (orderId, userId) =>
  apiReq(
    baseUrl + `api/Properties/DeleteSuiteOnBoarding/${orderId}/${userId}`,
    'DELETE',
    true,
    null
  );

export const fetchPropertyCatalog = (propertyId) =>
  apiReq(
    baseUrl + `api/Catalog/ItemsByProperty/${propertyId}`,
    'GET',
    true,
    null
  );
export const convertBase64ImageToURL = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ConvertBase64ToURL',
    'POST',
    true,
    params
  );

export const fetchKeyContacts = (propertyId) =>
  apiReq(
    baseUrl + 'api/Common/KeyContactsListByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const deleteOnboardingDocument = (id, userId) =>
  apiReq(
    baseUrl +
      'api/Properties/DeleteSuiteOnboardingChecklistDocument/' +
      id +
      '/' +
      userId,
    'DELETE',
    true,
    null
  );

// inbox APIs

export const fetchInboxList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Common/ServiceRequestAndSuiteMessages/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );
export const fetchAnnouncementList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Common/InboxAnnouncementList/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const fetchInboxSuitesList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitListbyUser/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const fetchChatMessages = (referenceId, referenceType) =>
  apiReq(
    baseUrl +
      'api/Common/ServiceRequestAndSuiteMessageDetails/' +
      referenceId +
      '/' +
      referenceType,
    'GET',
    true,
    null
  );

export const fetchChatUserInformation = (referenceId, referenceType) =>
  apiReq(
    baseUrl +
      'api/Common/MessagingusersInfo/' +
      referenceId +
      '/' +
      referenceType,
    'GET',
    true,
    null
  );

export const fetchInboxAnnouncementsByProperty = (
  userId,
  roleName,
  propertyId
) =>
  apiReq(
    baseUrl +
      'api/Common/InboxAnnouncementDetailsByProperty/' +
      userId +
      '/' +
      roleName +
      '/' +
      propertyId,
    'GET',
    true,
    null
  );

export const fetchInboxServiceRequestList = (
  userId,
  roleName,
  pageNumber,
  search
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestsServiceRequestListforMessages/' +
      userId +
      '/' +
      roleName +
      '/' +
      pageNumber +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const saveNewWalkthrough = (params) =>
  apiReq(baseUrl + 'api/Walkthrough/Walkthrough', 'POST', true, params);

export const deleteWalkthrough = (walkThroughId, userId) =>
  apiReq(
    baseUrl + `api/Walkthrough/Walkthrough/${walkThroughId}/${userId}`,
    'DELETE',
    true,
    null
  );

export const addBulkSuites = (params) =>
  apiReq(baseUrl + 'api/Properties/SuiteImport', 'POST', true, params);

export const addBulkCatalogs = (params) =>
  apiReq(baseUrl + 'api/Catalog/ImportItems', 'POST', true, params);

export const fetchSuiteFaqs = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/FaqsListBysuite/' + suiteId,
    'GET',
    true,
    null
  );

export const fetchSuiteTopFaqs = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/FaqsListBysuite/' + suiteId + '?$top=5',
    'GET',
    true,
    null
  );

export const updateUnreadToReadMessages = (
  referenceId,
  referenceType,
  userId
) =>
  apiReq(
    baseUrl +
      'api/Common/UpdateMessageReadStatus/' +
      referenceId +
      '/' +
      referenceType +
      '/' +
      userId,
    'POST',
    true,
    null
  );

export const validateURL = (email, code) =>
  apiReq(
    baseUrl +
      'api/UserManagement/PasswordLinkVerification/' +
      email +
      '/' +
      code,
    'GET',
    false,
    null
  );

export const fetchSpecifications = (itemId) =>
  apiReq(
    baseUrl + 'api/Catalog/SpecificationsListbyCatalog/' + itemId,
    'GET',
    true,
    null
  );

export const saveItemSpecifications = (params) =>
  apiReq(baseUrl + 'api/Catalog/CatalogSpecifications', 'POST', true, params);

export const updateItemSpecifications = (params) =>
  apiReq(baseUrl + 'api/Catalog/CatalogSpecifications', 'PUT', true, params);

export const deleteItemSpecifications = (specificationId) =>
  apiReq(
    baseUrl + 'api/Catalog/CatalogSpecifications/' + specificationId,
    'DELETE',
    true,
    null
  );

export const fetchkeypickupServiceRequests = (suiteId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/WalkthroughServiceRequestListForKeyPickup/' +
      suiteId,
    'GET',
    true,
    null
  );

export const fetchkeypickupImportantDocuments = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/SuiteImportantDocuments/' + suiteId,
    'GET',
    true,
    null
  );

export const getServiceRequestTags = () =>
  apiReq(baseUrl + 'api/ServiceRequest/Tags', 'GET', true, null);
export const saveCustomerFeedback = (params) =>
  apiReq(baseUrl + 'api/Common/SaveCustomerFeedback', 'POST', true, params);
export const fetchCustomerFeedback = (params) =>
  apiReq(baseUrl + 'api/Common/GetCustomerFeedback', 'POST', true, params);
export const saveServiceLog = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateServiceRequestEstimatedTime',
    'PUT',
    true,
    params
  );
export const ValidateCustomerFeedback = (ServiceRequestId) =>
  apiReq(
    baseUrl + 'api/common/ValidateCustomerFeedback/' + ServiceRequestId,
    'GET',
    true,
    null
  );
export const UpdateUserPDFName = (UserId, Id, Name) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/UpdateUserPDFName/' +
      UserId +
      '/' +
      Id +
      '/' +
      Name,
    'GET',
    true,
    null
  );
export const DeleteUserPDFRequest = (UserId, Id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/DeleteUserPDFRequest/' + UserId + '/' + Id,
    'GET',
    true,
    null
  );

export const saveArea = async (data) => {
  console.warn(
    'saveArea function is missing from client.js - this is a placeholder'
  );
  return { success: false, message: 'Not implemented' };
};

export const getServiceProviderProperties = (serviceProviderUserId) =>
  apiReq(
    baseUrl +
      `api/usermanagement/service-provider-properties/${serviceProviderUserId}`,
    'GET',
    true,
    null
  );
